/* global document */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import suitcss from '../../../helpers/suitcss';
import Switch from '../switch/Switch';
import ContentTabsItem from './TabItem';

const componentName = 'Tabs';
const Tabs = ({
  tabs,
  clickHandler,
  modifiers,
  theme,
  switchLabel,
  activeTab,
  colorScheme,
}) => {
  const tabRefs = useRef([]);
  const tablistRef = useRef(null);

  useEffect(() => {
    if (theme !== 'underlined') {
      return;
    }
    const line = document.querySelector(`.${componentName}-resultFilterLine`);
    const activeItem = document.querySelector('[class*="TabItem--active"]');
    if (!line || !activeItem) return;
    line.style.width = `${activeItem.getBoundingClientRect().width}px`;
    line.style.left = `${activeItem.offsetLeft}px`;
  });

  const handleTabClick = (index, activate = true, focus = true) => {
    if (activate) {
      clickHandler(tabs[index]);
    }

    if (focus && tabRefs.current[index]) {
      tabRefs.current[index].focus();
    }
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = index;

    // always activate the tab on keydown
    let activate = true;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = index === 0 ? tabs.length - 1 : index - 1;
        break;
      case 'ArrowRight':
        nextIndex = index === tabs.length - 1 ? 0 : index + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        activate = true;
        break;
      default:
        return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (tabRefs.current[nextIndex]) {
      handleTabClick(nextIndex, activate, true);
    }
  };

  // initial animation
  return (
    <div className={suitcss({
      modifiers: [theme, ...(Array.isArray(modifiers) ? modifiers : [])],
      componentName,
    })}
    >
      <div
        ref={tablistRef}
        role="tablist"
        className={suitcss({
          descendantName: 'navigationWrapper',
          modifiers: [theme],
          componentName,
        })}
      >
        {theme === 'underlined' && (
          <span
            className={suitcss({
              componentName,
              descendantName: 'resultFilterLine',
            }, this)}
          />
        )}
        {theme === 'switch' && tabs.length === 2 ? (
          <Switch
            isActive={activeTab}
            onChange={clickHandler}
            switchLabel={switchLabel}
          />
        ) : (
          tabs.map((tab, index) => (
            <ContentTabsItem
              ref={(el) => { tabRefs.current[index] = el; }}
              activeTab={tabs[activeTab].label}
              identifier={index}
              label={tab.label}
              onClick={() => handleTabClick(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              key={tab.label}
              colorScheme={colorScheme}
              theme={theme}
              isActive={index === activeTab}
              tabIndex={index === activeTab ? 0 : -1}
            />
          ))
        )}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.object),
  clickHandler: PropTypes.func,
  modifiers: PropTypes.arrayOf(PropTypes.string),
  theme: PropTypes.oneOf(['default', 'switch', 'icons', 'underlined']),
  switchLabel: PropTypes.string,
  activeTab: PropTypes.number,
  colorScheme: PropTypes.oneOf(['dark', 'medium', 'light', 'secondary']),
};

export default Tabs;
